import { Set, Map } from 'immutable';

import { getAffectingPropertyName, getFirstValue, getPropertyType } from '@/utils/taxonomy/ebay/properties';
import { getAttribute } from '@/utils/taxonomy';
import { shapeId } from '@/utils/listings/listings';
import { getSize } from '@/utils/iterable/getSize';
import { reduce } from '@/utils/iterable/reduce';
import { get } from '@/utils/iterable/get';

import { FILTER, MENU_ITEM, MENU_ITEMS, MENU_SECTION, MENU_SECTION_ITEMS } from '@/constants/bulkEdit';
import { TAXONOMY_PROPERTY, VALUE } from '@/constants/product';
import { EBAY, ETSY } from '@/constants/channels';
import { EMPTY } from '@/constants';
import { DATA } from '@/data';

export function allCategoriesLoaded({ categories, loaded }) {
  function reduceCategories(result, category) {
    return result && loaded.has(category);
  }

  return categories.reduce(reduceCategories, true);
}

export function getTaxonomyMenuItem(item) {
  return `taxonomy - ${item}`;
}

export function getEbayItemSpecific({ attribute: source = EMPTY.MAP, itemSpecifics, name, property }) {
  const attribute = source
    .set('multi', property.multiValued)
    .set('name', name)
    .set('path', 'properties')
    .set('required', property.required)
    .set('type', getPropertyType(property));

  function addOptions(item, option) {
    if (
      item.hasIn(['optionSet', option]) ||
      (item.get('type') === TAXONOMY_PROPERTY.BOOL && getSize(item.get('options')) === 2)
    ) {
      return item;
    }

    function updateOptionSet(optionSet = EMPTY.SET) {
      return optionSet.add(option);
    }

    function updateOptions(options = EMPTY.LIST) {
      return options.push(option);
    }

    return item.update('optionSet', updateOptionSet).update('options', updateOptions);
  }

  function addUniqueOptions(options = []) {
    return function update(item) {
      return options.reduce(addOptions, item);
    };
  }

  function reduceAffectedToOptions(result, options) {
    return result.concat(options);
  }

  if (getSize(property.options) === 0) {
    const affectedBy = getAffectingPropertyName({ property });

    if (affectedBy) {
      if (itemSpecifics) {
        const affectingPropertyValue = getFirstValue(itemSpecifics.getIn([VALUE, affectedBy, 'value']));

        if (affectingPropertyValue) {
          if (affectingPropertyValue in property.affectedBy[affectedBy]) {
            return attribute
              .set('affectedBy', affectedBy)
              .update(addUniqueOptions(property.affectedBy[affectedBy][affectingPropertyValue] || []));
          }

          if (property.text) {
            return attribute
              .set('affectedBy', affectedBy)
              .set('type', TAXONOMY_PROPERTY.TEXT);
          }
        }

        return attribute.set('affectedBy', affectedBy);
      }

      return attribute
        .set('affectedBy', affectedBy)
        .update(addUniqueOptions(reduce(property.affectedBy[affectedBy], [], reduceAffectedToOptions)));
    }

    return attribute;
  }

  return attribute.update(addUniqueOptions(property.options));
}

export function getEbayProductDetail({ attribute = EMPTY.MAP, name, property }) {
  return attribute
    .set('name', name)
    .set('path', 'productDetails')
    .set('required', property.required)
    .set('type', getPropertyType(property));
}

export function getEtsyTaxonomyAttribute({ attribute = EMPTY.MAP, category, channel, property, scale }) {
  const data = getAttribute({ category, channel, property, scale });

  function addOptions(options, item) {
    const { id, name } = item;

    if (options.hasIn(['byId', id])) return options;

    function updateById(byId = EMPTY.MAP) {
      return byId.set(id, Map({ id, name }));
    }

    function updateIds(ids = EMPTY.LIST) {
      return ids.push(id);
    }

    return options.update('byId', updateById).update('ids', updateIds);
  }

  function updateOptions(oldOptions = EMPTY.MAP) {
    return (data.availableOptions || []).reduce(addOptions, oldOptions);
  }

  function updateScales(oldScales = EMPTY.MAP) {
    function addScales(source, item) {
      let scales = source;
      const { id, name, suggestedOptionIds: suggested } = item;

      if (getSize(suggested)) {
        scales = scales.set('hasSuggested', true);
      }

      if (scales.hasIn(['byId', id])) {
        if (getSize(suggested)) {
          const { availableOptions: scaleOptions } = getAttribute({ category, channel, property, scale: id });

          if (getSize(scaleOptions)) {
            scales = scales.setIn(['byId', id, 'options'],
              scaleOptions.reduce(addOptions, scales.getIn(['byId', id, 'options'], EMPTY.MAP))
            );
          }
        }

        return scales;
      }

      function updateById(byId = EMPTY.MAP) {
        let result = Map({ id, name });

        if (getSize(suggested)) {
          result = result.set('suggested', Set(suggested));

          const { availableOptions: scaleOptions } = getAttribute({ category, channel, property, scale: id });

          if (getSize(scaleOptions)) {
            result = result.set('options', scaleOptions.reduce(addOptions, EMPTY.MAP));
          }
        }

        return byId.set(id, result);
      }

      function updateIds(ids = EMPTY.LIST) {
        return ids.push(id);
      }

      return scales.update('byId', updateById).update('ids', updateIds);
    }

    return (data.availableScales || []).reduce(addScales, oldScales);
  }

  function updateSelected(oldSelected = EMPTY.MAP) {
    function addSelected(selected, id) {
      if (selected.hasIn(['set', id])) return selected;

      function updateSet(set = EMPTY.SET) {
        return set.add(id);
      }

      function updateList(list = EMPTY.LIST) {
        return list.push(id);
      }

      return selected.update('set', updateSet).update('list', updateList);
    }

    return (data.selectedOptions || []).reduce(addSelected, oldSelected);
  }

  return attribute
    .set('name', data.displayName)
    .set('property', property)
    .set('required', data.required)
    .set('type', data.type)
    .update('options', updateOptions)
    .update('scales', updateScales)
    .update('selected', updateSelected);
}

export function updateMenuTaxonomyAttrubutes({ categories, channel, edited, marketplaceId, menu }) {
  function reduceCategories(result, category) {
    function updateAttribute(getAttributeFunction) {
      function updateCategories(attributeCategories = EMPTY.SET) {
        return attributeCategories.has(category) ? attributeCategories : attributeCategories.add(category);
      }

      return function update(menuItem) {
        const attribute = getAttributeFunction({ attribute: menuItem });
        const attributeCategories = updateCategories(attribute.get('categories'));
        let filters = EMPTY.MAP;

        if (!edited) {
          filters = filters.set(FILTER[channel].TAXONOMY_IDS, attributeCategories.toList().map(shapeId));
        }

        return attribute
          .delete('required')
          .set('categories', attributeCategories)
          .set('filters', filters)
          .set('selected', EMPTY.MAP);
      };
    }

    function updateMenuItem({ menuItem, updateFunction }) {
      if (result.byId.has(menuItem)) {
        result.byId = result.byId.update(menuItem, updateFunction);
      } else {
        const attribute = MENU_ITEMS[channel].get(MENU_ITEM.TAXONOMY);
        result.byId = result.byId.set(menuItem, updateFunction(attribute));
        result.ids = result.ids.push(menuItem);
      }
    }

    switch (channel) {
      case EBAY: {
        const taxonomyProperties = get([EBAY, marketplaceId, 'CATEGORIES', category, 'properties'])(DATA) || {};

        for (const name in taxonomyProperties) {
          if (taxonomyProperties.hasOwnProperty(name)) {
            const property = taxonomyProperties[name];

            updateMenuItem({
              menuItem: getTaxonomyMenuItem(name),
              updateFunction: updateAttribute(function getEbayAttribute(props) {
                return getEbayItemSpecific({ ...props, category, channel, name, property });
              }),
            });
          }
        }

        const taxonomyDetails = get([EBAY, marketplaceId, 'CATEGORIES', category, 'productDetails'])(DATA) || {};

        for (const name in taxonomyDetails) {
          if (taxonomyDetails.hasOwnProperty(name)) {
            const property = taxonomyDetails[name];

            updateMenuItem({
              menuItem: getTaxonomyMenuItem(name),
              updateFunction: updateAttribute(function getEbayAttribute(props) {
                return getEbayProductDetail({ ...props, category, channel, name, property });
              }),
            });
          }
        }

        break;
      }

      case ETSY: {
        const { availableAttributes = [] } = getAttribute({ category, channel });

        for (const item of availableAttributes) {
          const { id: property, scaleId: scale } = item;

          updateMenuItem({
            menuItem: getTaxonomyMenuItem(property),
            updateFunction: updateAttribute(function getEtsyAttribute(props) {
              return getEtsyTaxonomyAttribute({ ...props, category, channel, property, scale });
            }),
          });
        }

        break;
      }

      default: {
        break;
      }
    }

    return result;
  }

  const initial = {
    byId: menu.get('items'),
    ids: menu.getIn(['sections', 'items', MENU_SECTION.TAXONOMY], EMPTY.LIST),
  };

  const { byId, ids } = categories.reduce(reduceCategories, initial);

  return menu.set('items', byId).setIn(['sections', 'items', MENU_SECTION.TAXONOMY], ids);
}

export function clearMenuTaxonomyAttrubutes(menu) {
  function reduceTaxonomyItems(items, item) {
    return items.delete(item);
  }

  return menu
    .setIn(['sections', 'items', MENU_SECTION.TAXONOMY], EMPTY.LIST)
    .set('items',
      menu
        .getIn(['sections', 'items', MENU_SECTION.TAXONOMY], EMPTY.LIST)
        .reduce(reduceTaxonomyItems, menu.get('items'))
    );
}

export function removeConditionFromMenu(menu) {
  function updateSectionItems(items) {
    function filterItems(item) {
      return item !== MENU_ITEM.CONDITION;
    }

    return items.filter(filterItems);
  }

  return menu
    .updateIn(['sections', 'items', MENU_SECTION.LISTINGS], updateSectionItems)
    .deleteIn(['items', MENU_ITEM.CONDITION]);
}

export function updateMenuConditions({ categories, channel, marketplaceId, menu: source }) {
  function sortConditions(x, y) {
    return Math.sign(parseInt(x) - parseInt(y));
  }

  function reduceCategories(menu, category) {
    const data = get([channel, marketplaceId, 'CATEGORIES', category, 'conditions'])(DATA) || {};

    if (!data || getSize(data.byId) === 0) return menu;

    function reduceConditionIds(menuItem, conditionId) {
      if (menuItem.hasIn(['conditions', 'byId', conditionId])) return menuItem;

      function updateById(byId = EMPTY.MAP) {
        return byId.set(conditionId, data.byId[conditionId]);
      }

      function updateIds(ids = EMPTY.LIST) {
        return ids.push(conditionId).sort(sortConditions);
      }

      return menuItem
        .updateIn(['conditions', 'byId'], updateById)
        .updateIn(['conditions', 'ids'], updateIds);
    }

    return menu.setIn(['items', MENU_ITEM.CONDITION],
      data.ids.reduce(
        reduceConditionIds,
        menu.getIn(['items', MENU_ITEM.CONDITION], MENU_ITEMS[channel].get(MENU_ITEM.CONDITION))
      )
    );
  }

  const updated = categories.reduce(reduceCategories, source);

  function findConditionMenuIten(menuItem) {
    return menuItem === MENU_ITEM.CONDITION;
  }

  if (getSize(updated.getIn(['items', MENU_ITEM.CONDITION, 'conditions', 'ids'])) > 0) {
    if (updated.getIn(['sections', 'items', MENU_SECTION.LISTINGS]).findIndex(findConditionMenuIten) === -1) {
      return updated.setIn(['sections', 'items', MENU_SECTION.LISTINGS],
        MENU_SECTION_ITEMS[channel].get(MENU_SECTION.LISTINGS)
      );
    }

    return updated;
  }

  return removeConditionFromMenu(updated);
}
